// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

"use client"

import { useCallback, useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import { getPreferences, savePreferences } from "@/services/preferencesService"
import type { UserPreferences } from "@/types/preferences"

/** Lifecycle status of a preferences load/save. */
export type PreferencesStatus = "idle" | "loading" | "saving" | "saved" | "error"

/** Return shape of {@link usePreferences}. */
export interface UsePreferences {
  /** Load the caller's preferences from the server. */
  load: () => Promise<UserPreferences>
  /** Persist the given preferences document. */
  save: (prefs: UserPreferences) => Promise<void>
  /** Current lifecycle status. */
  status: PreferencesStatus
  /** Last error message, or null. */
  error: string | null
  /** Whether an ID token is available (i.e. the API can be called). */
  hasToken: boolean
}

/**
 * Hook wrapping the preferences service with load/save and a status flag.
 *
 * The editable draft state lives in the component; this hook only performs the
 * network calls and tracks status/error so the UI can render "Guardando…" /
 * "Preferencias guardadas".
 *
 * @returns load/save callbacks plus status, error, and token availability.
 */
export function usePreferences(): UsePreferences {
  const { token } = useAuth()
  const [status, setStatus] = useState<PreferencesStatus>("idle")
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<UserPreferences> => {
    if (!token) throw new Error("Se requiere autenticación para las preferencias.")
    setStatus("loading")
    setError(null)
    try {
      const prefs = await getPreferences(token)
      setStatus("idle")
      return prefs
    } catch (e) {
      setStatus("error")
      setError((e as Error).message)
      throw e
    }
  }, [token])

  const save = useCallback(
    async (prefs: UserPreferences): Promise<void> => {
      if (!token) throw new Error("Se requiere autenticación para las preferencias.")
      setStatus("saving")
      setError(null)
      try {
        await savePreferences(prefs, token)
        setStatus("saved")
      } catch (e) {
        setStatus("error")
        setError((e as Error).message)
        throw e
      }
    },
    [token]
  )

  return { load, save, status, error, hasToken: !!token }
}
